import React from 'react';
import { Pressable, Text, TextStyle, View, ViewStyle } from 'react-native';
import { Spinner } from '@/components/ui/Spinner';
import { Ionicons } from '@expo/vector-icons';
import { radius, spacing, type, useAppTheme } from '@/theme';
import { ScaledSheet } from '@/theme/responsive';

type Variant = 'primary' | 'secondary' | 'outlined' | 'ghost' | 'danger';
type Size = 'md' | 'sm';

interface Props {
  label: string;
  onPress?: () => void;
  variant?: Variant;
  size?: Size;
  icon?: keyof typeof Ionicons.glyphMap;
  iconPosition?: 'left' | 'right';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
  /** Let a long label run onto more lines. Off by default: a label stays on one line and shrinks a
   *  little to fit, because Android otherwise splits a word that is too wide ("Reschedul-e"). */
  multiline?: boolean;
}

export function Button({
  label,
  onPress,
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  disabled,
  loading,
  fullWidth = true,
  style,
  textStyle,
  multiline = false,
}: Props) {
  const { colors } = useAppTheme();
  const isDisabled = disabled || loading;

  const variantStyles: Record<Variant, { bg: string; textColor: string; border: string; borderWidth: number }> = {
    primary: { bg: colors.accentFill, textColor: colors.textOnAccent, border: 'transparent', borderWidth: 0 },
    secondary: { bg: colors.accentSoft, textColor: colors.accentDark, border: 'transparent', borderWidth: 0 },
    outlined: { bg: 'transparent', textColor: colors.textPrimary, border: colors.borderStrong, borderWidth: 1.5 },
    ghost: { bg: 'transparent', textColor: colors.textPrimary, border: 'transparent', borderWidth: 0 },
    danger: { bg: colors.errorSoft, textColor: colors.error, border: 'transparent', borderWidth: 0 },
  };

  const palette = variantStyles[variant];
  const iconColor = isDisabled ? colors.textTertiary : (textStyle?.color as string | undefined) ?? palette.textColor;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: isDisabled }}
      onPress={onPress}
      disabled={isDisabled}
      style={({ pressed }) => [
        styles.base,
        size === 'sm' ? styles.sm : styles.md,
        {
          backgroundColor: isDisabled ? colors.surfaceMuted : palette.bg,
          borderColor: isDisabled ? colors.border : palette.border,
          borderWidth: palette.borderWidth,
        },
        fullWidth && styles.fullWidth,
        pressed && !isDisabled && styles.pressed,
        style,
      ]}>
      {loading ? (
        <Spinner color={palette.textColor} size="small" />
      ) : (
        <View style={styles.content}>
          {icon && iconPosition === 'left' && <Ionicons name={icon} size={size === 'sm' ? 15 : 18} color={iconColor} />}
          <Text
            numberOfLines={multiline ? undefined : 1}
            adjustsFontSizeToFit={!multiline}
            minimumFontScale={0.75}
            style={[
              size === 'sm' ? type.buttonSm : type.button,
              { color: isDisabled ? colors.textTertiary : palette.textColor, flexShrink: 1, textAlign: 'center' },
              !isDisabled && textStyle,
            ]}>
            {label}
          </Text>
          {icon && iconPosition === 'right' && <Ionicons name={icon} size={size === 'sm' ? 15 : 18} color={iconColor} />}
        </View>
      )}
    </Pressable>
  );
}

const styles = ScaledSheet.create({
  base: {
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  md: { paddingVertical: 15, paddingHorizontal: spacing.xl },
  sm: { paddingVertical: 10, paddingHorizontal: spacing.lg },
  fullWidth: { width: '100%' },
  // maxWidth keeps a long label inside the button (it wraps) instead of spilling past its edges.
  content: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, justifyContent: 'center', maxWidth: '100%' },
  pressed: { opacity: 0.85 },
});
